import { useCallback, useEffect, useRef, useState } from 'react';
import { isMockMode } from '@/api';
import { ApiError } from '@/api/client';
import {
  electDigiLockerFallback,
  getRailLadder,
  relayDigiLockerCallback,
  startDigiLocker,
} from '@/api/identityRail';
import { useT } from '@/i18n/runtime';
import { useSessionStore } from '@/store/sessionStore';
import type { StepProps } from '../types';
import { AppBar } from '../components/AppBar';
import { Button } from '../components/Button';
import { isExpiredRelayError, relayWithBackoff } from './aadhaarRelayRetry';
import { useCheckStepFooter } from './useCheckStepFooter';

/**
 * SCREEN 02b · AADHAAR (DigiLocker) — SPEC-05 S4-8, the redirect-resume rail
 * step: AFTER consent (the rail refuses without a consent record — DPDP
 * ordering), BEFORE capture (UX_SPEC §3.2(b)). The government journey is a
 * FULL-PAGE, same-tab redirect (those properties block embedding); the store
 * persists to sessionStorage, so the same-tab return rehydrates mid-flow and
 * lands back on this step, which relays the provider's state+code to the be.
 *
 * Honesty rules, in order of importance:
 *  - provider unavailable (503 — the preview's `digilocker:not-configured`
 *    state) → a CALM fallback: continue, your documents are verified on the
 *    call. Never a dead end, never a fabricated success.
 *  - a completed government journey that fails mid-relay stays RETRYABLE (the
 *    be holds the attempt open) — the customer is never terminal-failed for a
 *    provider wobble.
 *  - VERIFIED is only ever what the be's ladder says — this screen never
 *    decides verification itself.
 */
/** sessionStorage key holding {sessionId, state} across the same-tab DigiLocker redirect. */
const PENDING_KEY = 'vcip.digilocker.pending';

export function Aadhaar({ tenant }: StepProps): JSX.Element {
  const { t } = useT();
  const real = !isMockMode();
  const sessionId = useSessionStore((s) => s.session.sessionId) as string;
  const customerName = useSessionStore((s) => s.session.customerName);
  // Shared OPTIONAL(skip)/REQUIRED(fallback) advance discipline. Records the
  // check guard so a plan-derived REQUIRED DIGILOCKER_AADHAAR can advance; a
  // no-op differentiator in the legacy 'aadhaar' frame step (canAdvance is true).
  const footer = useCheckStepFooter('DIGILOCKER_AADHAAR');

  type Phase =
    | { kind: 'checking' }
    | { kind: 'offer' }
    | { kind: 'relaying' }
    | { kind: 'verified' }
    | { kind: 'unavailable'; detail: string }
    | { kind: 'failed'; detail: string };
  const [phase, setPhase] = useState<Phase>({ kind: real ? 'checking' : 'offer' });
  const [busy, setBusy] = useState(false);
  const relayed = useRef(false);
  // Item 37: the HELD relay attempt. A relay that failed on a transient fault is
  // kept here so "Try again" RE-RELAYS the completed government journey (the be
  // holds the attempt open; the redeem is idempotent) instead of begin(), which
  // would discard the customer's completed DigiLocker consent. Cleared the
  // moment the server gives a verdict or says the attempt is dead (EXPIRED) —
  // only then does retry honestly mean "start again".
  const heldRelayRef = useRef<{ state: string; code: string | null } | null>(null);

  // The relay core, shared by the return leg and the failed-phase retry. Runs
  // the capped client backoff (~1s/3s/9s) BEFORE 'failed' ever renders, so a
  // provider blip never surfaces to the customer as an outage.
  const runRelay = useCallback(
    async (state: string, code: string | null): Promise<void> => {
      setPhase({ kind: 'relaying' });
      try {
        const attempt = await relayWithBackoff(() =>
          relayDigiLockerCallback(sessionId, state, code),
        );
        heldRelayRef.current = null; // the server answered — a verdict, not a wobble
        if (attempt.state === 'VERIFIED') {
          setPhase({ kind: 'verified' });
        } else if (attempt.state === 'EXPIRED') {
          setPhase({ kind: 'failed', detail: 'droom.aadhaar.failed.expired' });
        } else {
          setPhase({
            kind: 'failed',
            detail: attempt.failureReason ?? 'droom.aadhaar.failed.incomplete',
          });
        }
      } catch (err) {
        if (isExpiredRelayError(err)) {
          // The held attempt is genuinely dead server-side — begin() is the only path.
          heldRelayRef.current = null;
          setPhase({ kind: 'failed', detail: 'droom.aadhaar.failed.expired' });
          return;
        }
        // Transient after the cap: HOLD the attempt so retry re-relays it.
        heldRelayRef.current = { state, code };
        setPhase({ kind: 'failed', detail: 'droom.aadhaar.failed.relay' });
      }
    },
    [sessionId],
  );

  // KYCHUB DigiLocker generate-url requires firstName + mobile to seed the government
  // journey (KYCID·00001-00005-00007). These are PRE-FILL hints, not the verified
  // identity — only the DigiLocker consent result comes back. Prefill the given name
  // from the invite (first token of the customer's name); the customer confirms/edits.
  const [firstName, setFirstName] = useState(() => (customerName ?? '').trim().split(/\s+/)[0] ?? '');
  const [mobile, setMobile] = useState('');
  // Validate the INDIAN mobile format (must start 6–9), not just "10 digits" — a number like
  // 1234567890 is 10 digits but CRA refuses it, and a client-side check keeps that typo from ever
  // reaching the provider or showing the alarming "not available" card (KYCID·00001-00005-00008).
  const INDIAN_MOBILE = /^[6-9]\d{9}$/;
  const mobileDigits = mobile.replace(/\D/g, '');
  const mobileValid = INDIAN_MOBILE.test(mobileDigits);
  const formValid = firstName.trim().length > 0 && mobileValid;
  // A server-side input rejection (400) keeps the customer on the form with a fixable message,
  // distinct from the 503 "provider unavailable" calm-fallback.
  const [formError, setFormError] = useState<string | null>(null);

  // ── the RETURN leg: relay the single-use OAuth state from the return URL ──────
  // The raw state lives ONLY on the return URL (KYCID·00001-00005-00009): the BE mints it, the
  // adapter EMBEDS it in the provider redirectionUrl, and DigiLocker/Digitap round-trips it back as
  // ?state=<raw> (alongside its own txnId/success). The BE finds the attempt by that state's hash and
  // redeems via the persisted txnId. The start response does NOT expose the raw state (its `state`
  // field is the FSM state), so sessionStorage cannot carry it — the URL is authoritative. `code`
  // rides only for an OAuth provider that issues one.
  useEffect(() => {
    if (!real || relayed.current) return;
    const params = new URLSearchParams(window.location.search);
    const state = params.get('state');
    const code = params.get('code');
    if (!state) return;
    relayed.current = true;
    try {
      window.sessionStorage.removeItem(PENDING_KEY);
    } catch {
      /* best-effort clear */
    }
    // Scrub any provider params from the URL immediately — a reload must not re-relay
    // (the be redeem is idempotent anyway, but the address bar should not carry them).
    const clean = new URL(window.location.href);
    clean.searchParams.delete('state');
    clean.searchParams.delete('code');
    clean.searchParams.delete('txnId');
    clean.searchParams.delete('success');
    window.history.replaceState(null, '', clean.toString());
    // Capped backoff + held-attempt discipline live in runRelay (item 37).
    void runRelay(state, code);
  }, [real, sessionId, runRelay]);

  // ── initial state: a prior VERIFIED attempt auto-advances the step ─────────
  // Guarded by relayed.current so a same-tab RETURN (which the effect above claims first,
  // synchronously, since it is declared earlier) never races this into overriding 'relaying'.
  useEffect(() => {
    if (!real || relayed.current || phase.kind !== 'checking') return;
    let cancelled = false;
    getRailLadder(sessionId)
      .then((ladder) => {
        if (cancelled) return;
        if (ladder.some((a) => a.state === 'VERIFIED')) setPhase({ kind: 'verified' });
        else setPhase({ kind: 'offer' });
      })
      .catch(() => {
        if (!cancelled) setPhase({ kind: 'offer' });
      });
    return () => {
      cancelled = true;
    };
  }, [real, sessionId, phase.kind]);

  const begin = useCallback(async () => {
    setBusy(true);
    setFormError(null);
    try {
      // Surname PRE-FILL hint derived from the invite name (the tokens after the given name); empty when the
      // invite carried only one token. It is only a hint — the be sends the REAL value (never a fabricated
      // "Preview") and the verified surname comes back from the DigiLocker consent (de-demo P1-C).
      const lastName = (customerName ?? '').trim().split(/\s+/).slice(1).join(' ');
      const started = await startDigiLocker(sessionId, {
        firstName: firstName.trim(),
        lastName,
        mobile: mobileDigits,
      });
      // Persist the sessionId (NOT the state — the start response's `state` is the FSM state, not the
      // raw OAuth token; that only comes back on the return URL). The /idrail/callback bridge reads this
      // to recover which session to resume; the return leg then relays the URL state (00005-00009).
      try {
        window.sessionStorage.setItem(PENDING_KEY, JSON.stringify({ sessionId }));
      } catch {
        /* sessionStorage unavailable — the same-tab return still lands on this step with the URL state */
      }
      // Same-tab, full-page — the government journey happens on their property and returns to the
      // registered /idrail/callback with ?state=<raw>&txnId=&success=, which the bridge routes back here.
      window.location.assign(started.redirectUrl);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        // The provider is up but refused the input (a bad mobile) — customer-fixable. Stay on the
        // form (phase stays 'offer') with a clear message, NOT the 503 calm-fallback (00005-00008).
        setFormError('droom.aadhaar.err.form');
      } else if (err instanceof ApiError && err.status === 503) {
        setPhase({
          kind: 'unavailable',
          detail: 'droom.aadhaar.unavailable.detail',
        });
      } else if (err instanceof ApiError && err.status === 422) {
        // A state refusal (the be's pre-live band). The copy must never promise a "continue" this
        // REQUIRED step cannot offer (the footer suppresses plain skip — a client-side advance would
        // strand at queue:enter): retry is the lever, and the honest alternative is the live-OVD
        // discipline — the officer verifies the document with the customer on the call.
        setPhase({
          kind: 'failed',
          detail: 'droom.aadhaar.failed.state',
        });
      } else {
        setPhase({
          kind: 'failed',
          detail: 'droom.aadhaar.failed.start',
        });
      }
    } finally {
      setBusy(false);
    }
  }, [sessionId, firstName, mobileDigits]);

  // ── the audited outage fallback (KYCID·00001-00005-00019) ────────────────────
  // Offered ONLY in the 'unavailable' phase — i.e. after a start 503, which is exactly when the be has
  // committed a FAILED(PROVIDER_UNAVAILABLE:*) attempt row. The election POSTs to the be, which re-verifies
  // that server-written outage on record (a customer cannot forge it) and, on success, reports
  // FALLBACK_VERIFIED so the REQUIRED plan row is satisfied and queue:enter no longer strands. Only THEN do
  // we advance via footer.fallback() — the be's row, not this advance, is the real gate. A 409 means the be
  // saw no genuine outage (should not happen from this phase); keep the customer here with a retry message.
  const [fallbackError, setFallbackError] = useState<string | null>(null);
  const electFallback = useCallback(async () => {
    setBusy(true);
    setFallbackError(null);
    try {
      await electDigiLockerFallback(sessionId);
      footer.fallback();
    } catch (err) {
      // 409 = the be re-checked its ledger and found NO genuine outage backing
      // this election (item 37). Render that refusal honestly — the provider
      // looks reachable, so the lever is trying DigiLocker again — never spin
      // a generic "try again in a moment" over a definitive server answer.
      setFallbackError(
        err instanceof ApiError && err.status === 409
          ? 'droom.aadhaar.fallback.refused'
          : 'droom.aadhaar.fallback.error',
      );
    } finally {
      setBusy(false);
    }
  }, [sessionId, footer]);

  // Item 37: retry after 'failed' re-relays the HELD attempt when one exists
  // (a completed government journey is never discarded for a relay wobble);
  // only a genuinely dead/absent attempt begins a fresh DigiLocker journey.
  const retryAfterFailure = useCallback(async () => {
    const held = heldRelayRef.current;
    if (held) {
      await runRelay(held.state, held.code);
    } else if (formValid) {
      await begin();
    } else {
      // No held attempt and no usable pre-fill (a same-tab return wiped the
      // form): go back to the offer so the customer can re-enter and start a
      // fresh journey, instead of firing a begin() that can only 400.
      setPhase({ kind: 'offer' });
    }
  }, [runRelay, begin, formValid]);

  return (
    <div className="app-view">
      <AppBar tenant={tenant} subtitle={t('droom.aadhaar.subtitle')} />
      <main className="center-col" style={{ padding: 16 }} aria-label={t('droom.aadhaar.aria')}>
        {phase.kind === 'checking' || phase.kind === 'relaying' ? (
          <p className="sub" role="status">
            {phase.kind === 'relaying'
              ? t('droom.aadhaar.relaying')
              : t('droom.aadhaar.checking')}
          </p>
        ) : phase.kind === 'verified' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.aadhaar.verified.title')}</h2>
            <p className="sub">{t('droom.aadhaar.verified.sub')}</p>
            <Button onClick={() => footer.satisfy()}>{t('droom.action.continue')}</Button>
          </section>
        ) : phase.kind === 'unavailable' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.aadhaar.unavailable.title')}</h2>
            <p className="sub">{t(phase.detail)}</p>
            {/* OPTIONAL: skip. REQUIRED: retry, OR elect the audited outage fallback — the be re-verifies
                the server-written PROVIDER_UNAVAILABLE row and reports FALLBACK_VERIFIED, so queue:enter no
                longer strands (KYCID·00001-00005-00019). The advance is UX; the be's row is the gate. */}
            {footer.isOptional ? (
              <Button onClick={() => footer.skip()}>{t('droom.action.continue')}</Button>
            ) : (
              <>
                <Button disabled={busy} onClick={() => void electFallback()}>
                  {busy ? t('droom.action.pleaseWait') : t('droom.aadhaar.fallback.cta')}
                </Button>
                <Button variant="ghost" disabled={busy} onClick={() => void begin()}>
                  {t('droom.aadhaar.fallback.retry')}
                </Button>
                {fallbackError ? (
                  <p className="field-error" role="alert">
                    {t(fallbackError)}
                  </p>
                ) : null}
              </>
            )}
          </section>
        ) : phase.kind === 'failed' ? (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.aadhaar.failed.title')}</h2>
            <p className="sub">{t(phase.detail)}</p>
            {/* Retry re-relays a HELD completed journey; begins fresh only when
                the attempt is genuinely gone (item 37 — never discard consent). */}
            <Button disabled={busy} onClick={() => void retryAfterFailure()}>
              {t('droom.action.tryAgain')}
            </Button>
            {footer.isOptional ? (
              <Button variant="ghost" onClick={() => footer.skip()}>
                {t('droom.aadhaar.without')}
              </Button>
            ) : (
              <>
                {/* Item 37: a REQUIRED check whose rail failed was a DEAD END
                    (retry-only). Offer the AUDITED officer-verify election here
                    too — the be re-verifies eligibility server-side and 409s an
                    election with no genuine outage on record; that refusal is
                    rendered honestly below, never spun as success. */}
                <Button variant="ghost" disabled={busy} onClick={() => void electFallback()}>
                  {busy ? t('droom.action.pleaseWait') : t('droom.aadhaar.fallback.cta')}
                </Button>
                {fallbackError ? (
                  <p className="field-error" role="alert">
                    {t(fallbackError)}
                  </p>
                ) : null}
              </>
            )}
          </section>
        ) : (
          <section className="card stack" style={{ padding: 16 }}>
            <h2>{t('droom.aadhaar.offer.title')}</h2>
            <p className="sub">{t('droom.aadhaar.offer.body')}</p>
            {real ? (
              <>
                <label className="field">
                  <span className="field-label">{t('droom.aadhaar.label.name')}</span>
                  <input
                    className="field-input"
                    type="text"
                    autoComplete="given-name"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    placeholder={t('droom.aadhaar.placeholder.name')}
                    aria-label={t('droom.aadhaar.label.name')}
                  />
                </label>
                <label className="field">
                  <span className="field-label">{t('droom.aadhaar.label.mobile')}</span>
                  <input
                    className="field-input"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel"
                    maxLength={10}
                    value={mobile}
                    onChange={(e) => {
                      setMobile(e.target.value.replace(/\D/g, '').slice(0, 10));
                      setFormError(null);
                    }}
                    placeholder={t('droom.aadhaar.placeholder.mobile')}
                    aria-label={t('droom.aadhaar.label.mobile')}
                  />
                </label>
                {mobileDigits.length === 10 && !mobileValid ? (
                  <p className="field-error" role="alert">
                    {t('droom.aadhaar.err.mobile')}
                  </p>
                ) : null}
                {formError ? (
                  <p className="field-error" role="alert">
                    {t(formError)}
                  </p>
                ) : null}
                <p className="sub" style={{ fontSize: 12 }}>
                  {t('droom.aadhaar.hint')}
                </p>
                <Button disabled={busy || !formValid} onClick={() => void begin()}>
                  {busy ? t('droom.aadhaar.starting') : t('droom.aadhaar.cta')}
                </Button>
              </>
            ) : (
              <Button onClick={() => footer.satisfy()}>Continue (demo)</Button>
            )}
            {/* A REQUIRED DigiLocker must be completed — no client-side escape (it would advance with no
                VERIFIED rail and strand the customer at the BE queue:enter 422). OPTIONAL may skip. */}
            {footer.isOptional ? (
              <Button variant="ghost" onClick={() => footer.skip()}>
                {t('droom.aadhaar.skip')}
              </Button>
            ) : null}
          </section>
        )}
      </main>
    </div>
  );
}
